import { SnsWebSdkBaseConfig } from '@sumsub/websdk'
import SumsubWebSdk from '@sumsub/websdk-react'
import { SnsWebSdkOptions } from '@sumsub/websdk/types/types'
import React, { useCallback, useEffect, useMemo } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  SocketSumsubMessage,
  SumsubReviewStatus,
  SumsubStatusAnswer,
  SumsubWebhookType,
  handleSumsubMessages,
} from '@model/Sumsub'

import { useFetchOne } from '@hooks/useFetch'
import { useWebSocket } from '@hooks/useWebSocket'

import { Loading } from '../Loading/Loading'
import { detectThemeClientSide } from '../theme/Theme'

import styles from './Sumsub.module.scss'

export const Sumsub: React.FC = () => {
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)
  const theme = detectThemeClientSide()
  const navigate = useNavigate()

  const handleSocketMessage = (message: MessageEvent) => {
    const { data } = message

    if (typeof data === 'string') {
      try {
        const parsedData = JSON.parse(data)
        if (!isSumsubSocketMessage(parsedData)) {
          console.warn('Received invalid Sumsub WebSocket message:', parsedData)
          return
        }

        handleSumsubMessages(navigate, parsedData)
      } catch (error: unknown) {
        console.error('Error parsing WebSocket message:', error)
      }
    }
  }

  useWebSocket('sumsub', { onMessage: handleSocketMessage })

  const getSumsubAccessToken = useCallback(
    async (): Promise<string> => (await apiClient.getSumsubAccessToken()).token,
    [apiClient]
  )
  const { data: token, isLoading } = useFetchOne<string>(getSumsubAccessToken)

  const getSumsubStatus = useCallback(async () => await apiClient.getSumsubStatus(), [apiClient])
  const { data: sumsubStatus, isLoading: isSumsubStatusLoading } = useFetchOne(getSumsubStatus)

  useEffect(() => {
    if (sumsubStatus) {
      handleSumsubMessages(navigate, sumsubStatus)
    }
  }, [navigate, sumsubStatus])

  const sumsubConfig: SnsWebSdkBaseConfig = useMemo(() => {
    return {
      lang: locale,
      theme,
    }
  }, [locale, theme])

  const sumsubOptions: SnsWebSdkOptions = {
    enableScrollIntoView: false,
  }

  return (
    <Loading isLoading={isLoading || isSumsubStatusLoading} showLoadingIcon>
      <div className={styles.wrapper}>
        {token && (
          <SumsubWebSdk
            options={sumsubOptions}
            className={styles.sumsub}
            accessToken={token ?? ''}
            expirationHandler={getSumsubAccessToken}
            config={sumsubConfig}
            onError={(error) => console.log('sumsub error:', { error })}
          />
        )}
      </div>
    </Loading>
  )
}

const isSumsubSocketMessage = (o: any): o is SocketSumsubMessage => {
  return (
    o != null &&
    typeof o === 'object' &&
    // “Type” must be one of our webhook event types
    Object.values(SumsubWebhookType).includes(o.Type) &&
    // “reviewAnswer” must be one of our status answers
    Object.values(SumsubStatusAnswer).includes(o.reviewAnswer) &&
    // “ReviewStatus” likewise
    Object.values(SumsubReviewStatus).includes(o.ReviewStatus)
  )
}
